import { PolicyTypeCd } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { BillingDetails } from '@aaa-ncnu-ie/mypolicy-api-customers';
import {
  Stack,
  Card,
  Typography,
  Box,
  useMediaQuery,
  capitalize,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  unenrollInAutoPay,
  UnenrollInAutoPayArgs,
} from '../../../api/autoPay/unenroll';
import { useInstallmentsFeesQuery } from '../../../api/getInstallmentFees';
import { usePoliciesQuery } from '../../../api/getPolicies';
import AutoPayIcon from '../../../assets/icons/AutoPay';
import CircularWarning from '../../../assets/icons/CircularWarning';
import CardAlert from '../../../components/CardAlert';
import StatusChip from '../../../components/StatusChip';
import Button from '../../../components/styledComponents/Button';
import colors from '../../../constants/colors';
import { StatusType, EValueStatus } from '../../../constants/enums';
import { AutoPayMessages } from '../../../constants/paymentMessages';
import useAnalytics from '../../../hooks/useAnalytics';
import { useSnackbarsContext } from '../../../store/SnackbarProvider';
import { usePaymentsContext } from '../../../store/payments';
import { addSelectedBill } from '../../../store/paymentsReducer';
import theme from '../../../styles/theme';
import {
  policyTypeMap,
  policyTypeMapSelector,
} from '../../../utils/policyTypeUtil';
import AutoPayCardHeader from './AutoPayCardHeader';
import AutoPayInfoBar from './AutoPayInfoBar';
import AutoPayInfoGraphic from './AutoPayInfoGraphic';
import InstallmentFeesDialog from './InstallmentFeesDialog';
import UnenrollAutoPayDialog from './UnenrollDialog';

interface AutoPayCardProps {
  bill: BillingDetails;
}

const AutoPayCard = ({ bill }: AutoPayCardProps) => {
  const { track } = useAnalytics();
  const { data } = usePoliciesQuery();
  const foundPolicy = data?.policies?.find(
    (policy) => policy?.policyNumber === bill?.policyNumber
  );
  const { data: installmentFeesData } = useInstallmentsFeesQuery({
    productCode: foundPolicy?.productCode ?? '',
    riskState: foundPolicy?.riskState ?? '',
    policyFormCode: foundPolicy?.policyFormCode,
    effectiveDate: foundPolicy?.effectiveDate,
  });
  const installmentFee = installmentFeesData?.autoPaySavings;
  const queryClient = useQueryClient();
  const policyDetails = foundPolicy?.policyDetails;
  const policyStatus = foundPolicy?.policyStatus;
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const {
    autoPay,
    policyNumber,
    policyTypeCd,
    riskState,
    pastDue,
    fullProductCode,
  } = bill;
  const policyCancelled = policyStatus?.toLowerCase().includes('cancel');
  const policyExpired = policyStatus?.toLowerCase() === 'expired';
  const policyPastDue: boolean = pastDue && !(policyCancelled || policyExpired);
  const { enqueueSnackbar, closeSnackbar } = useSnackbarsContext();
  const { dispatch } = usePaymentsContext();
  const navigate = useNavigate();
  const enrolled = autoPay.enrolled;
  const [open, setOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const isIneligible = !enrolled && !autoPay.eligible;
  const policyTitleIndex = policyTypeMapSelector(policyTypeCd, fullProductCode);
  const policyTitle = policyTypeMap[policyTitleIndex].title;
  const paidByMortgagee =
    !enrolled && autoPay.reasonCode === 'NOT_ELIG_INV_BILL_TO';

  const unenroll = useMutation({
    mutationFn: (args: UnenrollInAutoPayArgs) => unenrollInAutoPay(args),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['bills'],
      });
      setConfirmDialogOpen(false);
      enqueueSnackbar({
        message: `${
          AutoPayMessages.STOP
        } ${policyTitle.toLowerCase()} policy ${policyNumber}.`,
        variant: StatusType.SUCCESS,
        autoHideDuration: 10000,
      });
    },
    onError: (err: unknown) => {
      console.error(err);
      setConfirmDialogOpen(false);
      enqueueSnackbar({
        message: AutoPayMessages.FAIL,
        variant: StatusType.ERROR,
        autoHideDuration: 10000,
      });
    },
  });

  const getBankAccountType = () => {
    const bankAccountType = autoPay?.account?.bankAccountType;

    if (bankAccountType === 'saving') {
      return 'savings';
    }

    return bankAccountType ?? '';
  };

  const displayPaymentMethod = autoPay?.card
    ? `Payment method: ${autoPay?.card?.cardType} ****${autoPay?.card?.creditCardNumberLast4}`
    : `Payment method: ${capitalize(getBankAccountType())} ****${
        autoPay?.account?.accountNumberLast4
      }`;

  const isExpired = autoPay?.card?.expired;

  const handleMakePayment =
    (edit = false) =>
    () => {
      track('AutoPay Modified', {
        location: 'Feature Card',
        link: edit ? 'Edit payment method' : 'Set up AutoPay',
        policy_number: policyNumber,
        policy_state: riskState,
        policy_type: policyTitle,
        setup_type: 'single policy',
        event_type: 'Link Accessed',
      });
      const eValue = foundPolicy?.eValue as EValueStatus;
      dispatch(addSelectedBill({ ...bill, policyType: policyTitle }));
      navigate('method', {
        state: { edit, eValue },
      });
    };

  const handleUnenrollAutoPay = async () => {
    if (!unenroll.isPending) {
      closeSnackbar();
      track('AutoPay Submitted', {
        location: 'Feature Card',
        status: 'Off',
        policy_number: policyNumber,
        policy_state: riskState,
        policy_type: policyTitle,
        event_type: 'User Information Entered',
      });
      await unenroll.mutateAsync({
        policy: {
          policyNumber,
          policyInfo: { productType: policyTypeCd as PolicyTypeCd },
        },
      });
    }
  };

  const showAutoPayInfoGraphic =
    !enrolled && !isIneligible && !paidByMortgagee && installmentFee;

  return (
    <Card
      component={Stack}
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        p: { tablet: 5, mobile: 2 },
        borderRadius: 2,
        height: 'auto',
      }}
    >
      {open && installmentFeesData && (
        <InstallmentFeesDialog
          open={open}
          closeDialog={() => setOpen(false)}
          installmentFees={installmentFeesData.installments}
        />
      )}
      <Stack spacing={4}>
        {(isIneligible || paidByMortgagee) && (
          <AutoPayInfoBar
            isIneligible={isIneligible && !paidByMortgagee}
            paidByMortgagee={paidByMortgagee}
          />
        )}
        {showAutoPayInfoGraphic && !isMobile && (
          <AutoPayInfoGraphic
            setOpen={setOpen}
            installmentFee={installmentFee}
          />
        )}
        <Stack spacing={3.5} direction="row" justifyContent="space-between">
          <AutoPayCardHeader
            policyNumber={policyNumber.toUpperCase()}
            policyTypeCd={policyTypeCd}
            vehicles={policyDetails?.vehicles}
            insuredAddress={policyDetails?.insuredAddress}
            isNanoPolicy={foundPolicy?.nanoPolicy}
            fullProductCode={fullProductCode}
          />
          <Stack direction="row" sx={{ height: 'fit-content' }} spacing={1}>
            <Box>
              {policyCancelled && <CardAlert value="Cancelled" />}
              {policyExpired && <CardAlert value="Expired" />}
              <Typography
                sx={{
                  fontFamily: 'Cabin',
                  fontKerning: 'normal',
                  fontWeight: 500,
                  fontSize: 14,
                  letterSpacing: '0.2px',
                  color: colors.csaaDark,
                  backgroundColor: colors.csaaNeutralBackground,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1,
                  width: 89,
                }}
              >
                {enrolled ? 'AutoPay on' : 'AutoPay off'}
              </Typography>
            </Box>
            {policyPastDue && <StatusChip alert label="Past due" />}
          </Stack>
        </Stack>
        {showAutoPayInfoGraphic && isMobile && (
          <AutoPayInfoGraphic
            setOpen={setOpen}
            installmentFee={installmentFee}
          />
        )}
        <Stack
          direction={{ mobile: 'column', tablet: 'row' }}
          spacing={{ mobile: 2, tablet: 0 }}
          justifyContent="space-between"
        >
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontWeight: 700,
              fontSize: { mobile: 33, tablet: 44 },
              color: colors.csaaBlack,
            }}
          >
            {policyTitle} Policy
          </Typography>
          {enrolled && (
            <Stack direction={{ mobile: 'column', tablet: 'row' }} spacing={2}>
              <Button
                data-testid="StopAutoPayButton"
                variant="outlined"
                color="inherit"
                disableRipple
                sx={{
                  borderWidth: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 18,
                  color: 'text.secondary',
                  px: 4,
                  py: 0.5,
                  whiteSpace: 'nowrap',
                }}
                onClick={() => {
                  setConfirmDialogOpen(true);
                }}
              >
                Stop AutoPay
              </Button>
              <Button
                data-testid="EditPaymentButton"
                variant="contained"
                sx={{
                  whiteSpace: 'nowrap',
                  height: { mobile: 36, tablet: 42 },
                }}
                onClick={handleMakePayment(true)}
              >
                Edit payment method
              </Button>
            </Stack>
          )}
          {!enrolled && (
            <Button
              data-testid="SetupAutoPayButton"
              variant="contained"
              sx={{
                whiteSpace: 'nowrap',
                height: { mobile: 36, tablet: 42 },
              }}
              onClick={handleMakePayment()}
              disabled={isIneligible || paidByMortgagee}
            >
              Set up AutoPay
            </Button>
          )}
        </Stack>
        {enrolled && (
          <Stack spacing={2}>
            <Typography
              className="csaa-qm-block"
              sx={{
                fontSize: { mobile: 14, tablet: 16 },
                fontWeight: 400,
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                color: colors.csaaDark,
              }}
            >
              {displayPaymentMethod}
            </Typography>
            {isExpired ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularWarning sx={{ height: 20, width: 20 }} />
                <Typography
                  sx={{
                    fontSize: { mobile: 16, tablet: 18 },
                    fontWeight: 400,
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    color: colors.csaaDark,
                  }}
                >
                  AutoPay payment method has expired.
                </Typography>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <AutoPayIcon />
                <Typography
                  sx={{
                    fontSize: { mobile: 16, tablet: 18 },
                    fontWeight: 400,
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    color: colors.csaaDark,
                  }}
                >
                  AutoPay is scheduled for your installment date.
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
      {confirmDialogOpen && (
        <UnenrollAutoPayDialog
          open={confirmDialogOpen}
          confirmUnenroll={handleUnenrollAutoPay}
          closeDialog={() => setConfirmDialogOpen(false)}
          isLoading={unenroll.isPending}
          isEValue={[EValueStatus.ACTIVE, EValueStatus.PENDING].includes(
            foundPolicy?.eValue?.toUpperCase() as EValueStatus
          )}
        />
      )}
    </Card>
  );
};

export default AutoPayCard;
